/**
 * <ugc-carousel> — scroll-snap carousel with prev/next arrows and dots.
 * Works alongside <video-player> on each card (see video-section.js).
 */

class UgcCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.cards = Array.from(this.querySelectorAll('.ugc-card'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    if (!this.track || this.cards.length === 0) return;

    const initialIdx = Math.floor((this.cards.length - 1) / 2);
    this.activeIndex = initialIdx;
    this.applyDistances(initialIdx);
    this.bindCardClicks();

    if (this.cards.length <= 1) return;

    this.prevBtn?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index)));
    });

    this.attachScrollListener();
    this.updateNav();

    if (initialIdx > 0) {
      requestAnimationFrame(() => this.goTo(initialIdx, false));
    }
  }

  bindCardClicks() {
    this.cards.forEach((card, idx) => {
      card.addEventListener('click', (e) => {
        if (idx !== this.activeIndex) {
          e.preventDefault();
          e.stopPropagation();
          this.goTo(idx);
          return;
        }
        const player = card.querySelector('video-player');
        if (player && typeof player.toggle === 'function') {
          e.preventDefault();
          e.stopPropagation();
          player.toggle();
        }
      }, true);
    });
  }

  goTo(index, smooth = true) {
    const max = this.cards.length - 1;
    const clamped = Math.max(0, Math.min(max, index));
    const card = this.cards[clamped];
    if (!card) return;
    const target = card.offsetLeft - (this.track.clientWidth - card.clientWidth) / 2;
    this.track.scrollTo({
      left: Math.max(0, target),
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  attachScrollListener() {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        this.detectActiveByCenter();
      });
    };
    this.track.addEventListener('scroll', onScroll, { passive: true });
    this.detectActiveByCenter();
  }

  detectActiveByCenter() {
    const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
    let nearestIdx = 0;
    let nearestDist = Infinity;
    this.cards.forEach((card, idx) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const dist = Math.abs(cardCenter - trackCenter);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestIdx = idx;
      }
    });
    this.setActive(nearestIdx);
  }

  setActive(i) {
    if (i === this.activeIndex) return;
    this.activeIndex = i;
    this.applyDistances(i);
    this.dots.forEach((dot) => {
      const isActive = Number(dot.dataset.index) === i;
      dot.classList.toggle('ugc-carousel__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
    this.pauseInactive(i);
    this.updateNav();
  }

  applyDistances(activeIdx) {
    this.cards.forEach((card, idx) => {
      const distance = Math.abs(idx - activeIdx);
      card.dataset.distance = String(Math.min(distance, 3));
    });
  }

  pauseInactive(activeIdx) {
    this.cards.forEach((card, idx) => {
      if (idx === activeIdx) return;
      const video = card.querySelector('video');
      if (video && !video.paused) video.pause();
      const player = card.querySelector('video-player');
      if (player) player.removeAttribute('playing');
    });
  }

  updateNav() {
    if (this.prevBtn) this.prevBtn.disabled = this.activeIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.activeIndex === this.cards.length - 1;
  }
}

if (!customElements.get('ugc-carousel')) {
  customElements.define('ugc-carousel', UgcCarousel);
}
